import { faFile } from "@fortawesome/pro-regular-svg-icons";
import { first } from "lodash-es";

import { useFilesQuery } from "src/api/filesApi/queries";
import { BaseFilePill } from "src/base-components/FilePill";
import { useCaseBlockScrollTo } from "src/cases/caseView/CaseBlockScrollHooks";
import { HistorySingleEntry } from "src/cases/caseView/history/HistoryItemFrame";
import { useCase } from "src/cases/queries/cases";
import { HistoryEntryBlockFilesGroup } from "src/cases/queries/casesTypes";
import { FileReference } from "src/clients/case-management";
import { GetFilesEnvFilesGetEnvEnum } from "src/clients/files";
import { Pill } from "src/design-system/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import { CasePageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const BlockFilesUpdatedGroup: React.FC<{
  entry: HistoryEntryBlockFilesGroup;
  isLast: boolean;
}> = ({ entry, isLast }) => {
  "use memo";

  const { events } = entry;
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const { data: caseData } = useCase(caseId);
  const scrollToBlock = useCaseBlockScrollTo();

  // The group is built newest-first, so the head is the most recent change.
  const latestEvent = first(events);

  if (!latestEvent) {
    // If such happened, then we have nothing to display
    return null;
  }

  // Consolidate all added and removed files from all events in the group
  const allAddedFiles = events.flatMap((event) => event.event_data.added_files);
  const allRemovedFiles = events.flatMap(
    (event) => event.event_data.removed_files,
  );

  const eventData = latestEvent.event_data;
  const blockKey = eventData.block_key;

  // Resolve block_key to block name
  const blockConfig =
    caseData?.data.template_version?.case_blocks_configs?.[blockKey];
  const blockName = blockConfig?.title ?? blockKey;

  const fileEnv =
    caseData?.data.env === "sandbox"
      ? GetFilesEnvFilesGetEnvEnum.SANDBOX
      : GetFilesEnvFilesGetEnvEnum.LIVE;

  // Create tooltip content with Added/Removed sections
  const tooltipBody = (
    <div className="flex flex-col gap-y-3">
      {allAddedFiles.length > 0 && (
        <div className="flex flex-col gap-y-1.5">
          <div className="font-inter-semibold-13px">Added Files</div>
          <FileList env={fileEnv} files={allAddedFiles} />
        </div>
      )}
      {allRemovedFiles.length > 0 && (
        <div className="flex flex-col gap-y-1.5">
          <div className="font-inter-semibold-13px">Removed Files</div>
          <FileList env={fileEnv} files={allRemovedFiles} />
        </div>
      )}
    </div>
  );

  // Build headline with pill wrapped in Tooltip
  const headline = (
    <>
      <Tooltip body={tooltipBody} asChild>
        <span>
          <Pill dataLoc="block-files-pill" size="sm" variant="gray">
            <Pill.Icon icon={faFile} />
            <Pill.Text>Files</Pill.Text>
          </Pill>
        </span>
      </Tooltip>{" "}
      in{" "}
      <Pill size="sm" variant="gray" onClick={() => scrollToBlock(blockKey)}>
        <Pill.Text>{blockName}</Pill.Text>
      </Pill>{" "}
      were modified
    </>
  );

  return (
    <HistorySingleEntry
      actorId={eventData.updated_by_user_id}
      eventType={latestEvent.event_type}
      headline={headline}
      icon={faFile}
      isLast={isLast}
      timestamp={latestEvent.timestamp}
    />
  );
};

const MAX_DISPLAY_COUNT = 5;

const FileList: React.FC<{
  files: FileReference[];
  env: GetFilesEnvFilesGetEnvEnum;
}> = ({ files, env }) => {
  "use memo";

  const displayedFiles = files.slice(0, MAX_DISPLAY_COUNT);
  const remainingCount = files.length - MAX_DISPLAY_COUNT;

  // Only the files that are actually rendered are worth resolving.
  const { data, isLoading } = useFilesQuery(
    displayedFiles.map(({ id }) => id),
    env,
  );
  const namesById = new Map(
    (data?.data.data ?? []).map((file) => [file.id, file.attributes.name]),
  );

  return (
    <div className="flex flex-wrap gap-1">
      {displayedFiles.map((file) => (
        <BaseFilePill
          key={file.id}
          copyValue={file.id}
          dataLoc={`block-file-${file.id}`}
          fileName={
            isLoading ? "Loading ..." : (namesById.get(file.id) ?? file.id)
          }
          fontType={namesById.has(file.id) ? "text" : "code-12"}
          variant="dark-gray"
        />
      ))}
      {remainingCount > 0 && (
        <Pill size="sm" variant="dark-gray">
          <Pill.Text>+{remainingCount}</Pill.Text>
        </Pill>
      )}
    </div>
  );
};
